import cn from 'classnames'
import { memo, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Tooltip } from 'core-components/library/tooltip'

import { scrollOffset } from './configs'
import { FieldGroupColor, FieldGroupRounded, IFieldGroupProps } from './models'
import { fieldGroupTranslations } from './translation'

import styles from './FieldGroup.module.scss'

export const FieldGroup = memo<PropsWithChildren<IFieldGroupProps>>(
  ({
    classNames,
    children,
    isHidden,
    title,
    size = 3,
    isCollapsible = false,
    isCollapsed = false,
    hasScrollTo = false,
    hasBorder = false,
    color = FieldGroupColor.Default,
    rounded = FieldGroupRounded.Medium,
  }) => {
    /** ------------------------------- Variables ------------------------------- */

    const [isContentVisible, setIsContentVisible] = useState(!isCollapsed)
    const fieldGroupRef = useRef<HTMLDivElement>(null)
    const isFirstRender = useRef(true)

    const collapseButton = isContentVisible ? (
      <Tooltip tipContent={() => <Translation text={fieldGroupTranslations.collapsible.hide} />}>
        <i className="kbase kbase-caret-top" />
      </Tooltip>
    ) : (
      <Tooltip tipContent={() => <Translation text={fieldGroupTranslations.collapsible.show} />}>
        <i className="kbase kbase-caret-down" />
      </Tooltip>
    )
    const hasTitle = Boolean(title)
    const fieldGroupTitleStyles = useMemo(
      () =>
        cn(
          {
            [styles.hidden]: !hasTitle,
            [styles.groupTitleCollapsible]: isCollapsible,
            [styles.borderBottom]: isContentVisible,
          },
          classNames?.groupTitle,
          styles.groupTitle
        ),
      [classNames?.groupTitle, hasTitle, isContentVisible, isCollapsible]
    )
    const fieldGroupTitleRole = useMemo(
      () => (isCollapsible ? 'button' : 'region'),
      [isCollapsible]
    )
    const fieldGroupTitle = useMemo(() => {
      if (title && typeof title === 'string') {
        return <Translation text={title} span />
      }
      return title
    }, [title])

    /** ------------------------------- Handlers ------------------------------- */

    const handleOnCollapseButtonClick = useCallback(() => {
      if (isCollapsible) setIsContentVisible(!isContentVisible)
    }, [isCollapsible, isContentVisible])

    /** ------------------------------- Render ------------------------------- */

    useEffect(() => {
      // Skip the first render
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      if (hasScrollTo && isContentVisible && fieldGroupRef && fieldGroupRef.current) {
        const [rect] = fieldGroupRef.current.getClientRects()
        window.scrollTo({
          top: rect.y - scrollOffset,
          behavior: 'smooth',
        })
      }
    }, [hasScrollTo, isContentVisible])

    if (isHidden) {
      return null
    }

    return (
      <div
        ref={fieldGroupRef}
        className={cn(
          classNames?.group,
          styles.group,
          styles.rounded,
          styles[rounded],
          styles[color],
          {
            [styles.border]: hasBorder,
          }
        )}
      >
        <div
          className={cn(classNames?.groupTitle, fieldGroupTitleStyles)}
          role={fieldGroupTitleRole}
          onClick={handleOnCollapseButtonClick}
        >
          {fieldGroupTitle}
          <span
            className={cn({ [styles.hidden]: !isCollapsible }, styles.collapseButton)}
            onClick={handleOnCollapseButtonClick}
          >
            {collapseButton}
          </span>
        </div>
        <div
          className={cn({
            [styles.isCollapsed]: !isContentVisible,
          })}
        >
          <div
            className={cn(classNames?.groupFields, styles.groupFields)}
            style={size ? { gridTemplateColumns: `repeat(${size}, 1fr)` } : {}}
          >
            {children}
          </div>
        </div>
      </div>
    )
  }
)

FieldGroup.displayName = 'FieldGroup'
